import { get, set, del } from "idb-keyval";

/**
 * Robust storage utility to manage data between IndexedDB and localStorage.
 * IndexedDB is used for larger data like cart, wishlist, and orders.
 * localStorage is used for small, sensitive data like auth tokens/status.
 */

// --- IndexedDB Helpers (for large data) ---

export const saveToIndexedDB = async (key: string, value: any) => {
  try {
    await set(key, value);
  } catch (error) {
    console.error(`Error saving ${key} to IndexedDB:`, error);
  }
};

export const getFromIndexedDB = async <T>(key: string): Promise<T | null> => {
  try {
    const value = await get(key);
    return (value as T) ?? null;
  } catch (error) {
    console.error(`Error getting ${key} from IndexedDB:`, error);
    return null;
  }
};

export const removeFromIndexedDB = async (key: string) => {
  try {
    await del(key);
  } catch (error) {
    console.error(`Error removing ${key} from IndexedDB:`, error);
  }
};

// --- localStorage Helpers (for auth/small data) ---

export const saveToLocalStorage = (key: string, value: any) => {
  if (typeof window === "undefined") return;
  try {
    const stringValue =
      typeof value === "string" ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

export const getFromLocalStorage = (key: string) => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(key);
};

export const removeFromLocalStorage = (key: string) => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(key);
};

// --- Sync Helpers ---

export const syncDataToServer = async (
  email: string,
  data: { cart?: any[]; wishlist?: any[]; orders?: any[]; profile?: any },
) => {
  try {
    await fetch("/api/user/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, ...data }),
    });
  } catch (error) {
    console.error("Error syncing to server:", error);
  }
};

export const fetchDataFromServer = async (email: string) => {
  try {
    const response = await fetch(`/api/user/sync?email=${email}`);
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.error("Error fetching from server:", error);
  }
  return null;
};
